/*
  The three states §6.2 requires: loading (skeleton), empty, error. "No bare blank table."

  Three small siblings in one file because they are always chosen between, never used
  alone — resolveTableStatus() in tableState.ts picks one and DataTable renders it.

  The error state is the one that needed plumbing rather than markup: tables today accept
  only `data` and `isFetching` (GameSetsTable.tsx:32-36, CardViewTable.tsx:34-37) and the
  views throw the RTK Query error away (GameView.tsx:48, CardView.tsx:27), so there is no
  value anywhere in the app that could put a table into an error state. DataTable takes an
  `error` prop for exactly that reason; tasks 7 to 10 stop discarding it.

  The skeleton keeps the table's shape — same header, same column count, a bar per cell —
  so rows arriving do not shove the page. A spinner would collapse the table to nothing
  and then re-expand it.
*/

import { ReactNode } from "react";
import { AlertTriangle, SearchX } from "lucide-react";

import { FOCUS_RING } from "components/styles";

import { columnAlignClass } from "./columns";
import {
  STACKED_CARD,
  TABLE_BODY_CELL,
  TABLE_CELL_PADDING_TIGHT,
  TABLE_CELL_PADDING,
} from "./tableStyles";
import { DataTableColumn } from "./types";

/* Roughly a line of text, in the one colour that is subtle in both themes. */
const SKELETON_BAR = "h-4 rounded bg-pcg-raised";

const MESSAGE_BLOCK = "flex flex-col items-center gap-2 px-4 py-10 text-center";

export interface DataTableEmptyProps {
  /* The old no-results row's wording is the default, so nothing reads differently. */
  title?: string;
  description?: ReactNode;
}

export const DataTableEmpty = (props: DataTableEmptyProps) => {
  const { title = "No results found", description } = props;

  return (
    <div className={MESSAGE_BLOCK}>
      <SearchX aria-hidden="true" className="h-6 w-6 text-pcg-textMuted" />
      <p className="text-sm font-semibold text-pcg-text">{title}</p>
      {description ? (
        <p className="max-w-prose text-sm text-pcg-textMuted">{description}</p>
      ) : null}
    </div>
  );
};

export interface DataTableErrorProps {
  title?: string;
  description?: ReactNode;
  /* Wire it to RTK Query's refetch. Omitted, no button is rendered. */
  onRetry?: () => void;
  retryLabel?: string;
}

/*
  role="alert" so the failure is announced rather than silently replacing the rows. The
  message never carries the server's own text: a public site does not put a 500 body in
  front of a visitor, and the useful half is in the network tab anyway.
*/
export const DataTableError = (props: DataTableErrorProps) => {
  const {
    title = "Something went wrong",
    description = "This table could not be loaded. Please try again.",
    onRetry,
    retryLabel = "Try again",
  } = props;

  return (
    <div role="alert" className={MESSAGE_BLOCK}>
      <AlertTriangle aria-hidden="true" className="h-6 w-6 text-pcg-danger" />
      <p className="text-sm font-semibold text-pcg-text">{title}</p>
      {description ? (
        <p className="max-w-prose text-sm text-pcg-textMuted">{description}</p>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className={`mt-1 rounded-md border border-pcg-border px-3 py-1.5 text-sm font-semibold text-pcg-goldText hover:bg-pcg-raised ${FOCUS_RING}`}
        >
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
};

export interface DataTableSkeletonProps<TRow> {
  columns: readonly DataTableColumn<TRow>[];
  /* How many placeholder rows. Match the page size and the table does not resize. */
  rows?: number;
}

/*
  Placeholder <tr>s, for inside a <tbody>. aria-hidden because the bars say nothing; the
  "Loading" announcement is DataTable's own sr-only status.

  The widths alternate so the block reads as text rather than as a grid of identical
  blocks, and a right-aligned (numeric) column's bar sits right, where its number will.
*/
export const DataTableSkeletonRows = <TRow,>(
  props: DataTableSkeletonProps<TRow>
) => {
  const { columns, rows = 5 } = props;

  return (
    <>
      {Array.from({ length: rows }).map((_unused, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
          {columns.map((column, columnIndex) => (
            <td
              key={column.id}
              className={`${TABLE_BODY_CELL} ${columnAlignClass(column)} ${
                column.compact ? TABLE_CELL_PADDING_TIGHT : ""
              }`}
            >
              <span
                className={`inline-block w-full ${SKELETON_BAR} animate-pulse`}
                style={{ maxWidth: (rowIndex + columnIndex) % 2 === 0 ? "80%" : "60%" }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/* The same, for stacked mode: cards the size the real cards will be. */
export const DataTableSkeletonCards = <TRow,>(
  props: DataTableSkeletonProps<TRow>
) => {
  const { columns, rows = 3 } = props;

  return (
    <ul aria-hidden="true" className="flex flex-col gap-3">
      {Array.from({ length: rows }).map((_unused, rowIndex) => (
        <li key={`skeleton-${rowIndex}`} className={STACKED_CARD}>
          <div className="divide-y divide-pcg-borderSoft">
            {columns.map((column) => (
              <div
                key={column.id}
                className={`${TABLE_CELL_PADDING} flex items-center justify-between gap-3`}
              >
                <span className={`w-24 ${SKELETON_BAR} animate-pulse`} />
                <span className={`w-16 ${SKELETON_BAR} animate-pulse`} />
              </div>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
};
